'use client';

import {ModalBackdrop} from 'common/components/modal_backdrop';
import {AuthForm} from 'iq-auth/components/auth_form';
import {useSignupCta} from 'iq-auth/hooks';
import {ShareTakeHomeModal} from 'iq-takehomes/components/share_take_home_modal';
import {
  trackTakeHomePaywallViewed,
  trackTakeHomeShareOpened,
  trackTakeHomeSubscribeClicked,
} from 'iq-takehomes/tracking';
import {PaywallModalContent} from 'iq-upselling/components/paywall_modal_content';
import {useSubscribeHref} from 'iq-upselling/hooks';
import {useEffect, useState} from 'react';

const PRIMARY_CTA_CLASS =
  'w-full py-3 bg-iq-red text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity block text-center';
const SECONDARY_CTA_CLASS =
  'w-full px-6 py-2.5 border border-iq-grey-light text-iq-black text-sm font-semibold rounded-md hover:bg-iq-grey-100 transition-colors block text-center';

function PublicContent({onSignup, onShare}: {onSignup: () => void; onShare: () => void}) {
  return (
    <PaywallModalContent
      title='Unlock this take-home'
      description="You've used your free preview. Create a free account to keep going — or share a take-home you've received to unlock access right now."
      primaryAction={
        <button onClick={onSignup} className={PRIMARY_CTA_CLASS}>
          Sign up for free
        </button>
      }
      secondaryAction={
        <button onClick={onShare} className={SECONDARY_CTA_CLASS}>
          Share a take-home to unlock
        </button>
      }
    />
  );
}

function FreeContent({onShare}: {onShare: () => void}) {
  const subscribeHref = useSubscribeHref();
  return (
    <PaywallModalContent
      title='Upgrade your plan'
      description="You've used your free take-home access. Upgrade to Coder or Premium to unlock more projects."
      primaryAction={
        <a href={subscribeHref} onClick={() => { void trackTakeHomeSubscribeClicked('barrier'); }} className={PRIMARY_CTA_CLASS}>
          Upgrade now
        </a>
      }
      secondaryAction={
        <button onClick={onShare} className={SECONDARY_CTA_CLASS}>
          Share a take-home to unlock access
        </button>
      }
    />
  );
}

function CoderContent({onShare}: {onShare: () => void}) {
  const subscribeHref = useSubscribeHref();
  return (
    <PaywallModalContent
      title='Upgrade to Premium'
      description="You've used your Coder plan take-home access. Upgrade to Premium for unlimited projects."
      primaryAction={
        <a href={subscribeHref} onClick={() => { void trackTakeHomeSubscribeClicked('barrier'); }} className={PRIMARY_CTA_CLASS}>
          Upgrade to Premium
        </a>
      }
      secondaryAction={
        <button onClick={onShare} className={SECONDARY_CTA_CLASS}>
          Share a take-home to unlock access
        </button>
      }
    />
  );
}

type GateMode = 'public' | 'free' | 'coder';

interface Props {
  gateMode: GateMode;
}

export function LockedTakeHomeGate({gateMode}: Props) {
  const [signupOpen, setSignupOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const startSignup = useSignupCta('take_home_gate');

  useEffect(() => {
    void trackTakeHomePaywallViewed(gateMode);
  }, [gateMode]);

  function openShare() {
    void trackTakeHomeShareOpened('barrier');
    setShareModalOpen(true);
  }

  return (
    <>
      {/* Keep the blurred backdrop mounted while sharing; hide only the gate card so the share modal isn't stacked over it. */}
      <ModalBackdrop
        className='bg-white/85 backdrop-blur-sm'
        zIndex={30}
        aria-label='Take-home access required'>
        {!shareModalOpen && signupOpen && (
          <div className='relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8'>
            <AuthForm
              onSuccess={() => window.location.reload()}
              onBack={() => setSignupOpen(false)}
            />
          </div>
        )}
        {!shareModalOpen && !signupOpen && gateMode === 'public' && (
          <PublicContent
            onSignup={() => startSignup(() => setSignupOpen(true))}
            onShare={openShare}
          />
        )}
        {!shareModalOpen && !signupOpen && gateMode === 'free' && (
          <FreeContent onShare={openShare} />
        )}
        {!shareModalOpen && !signupOpen && gateMode === 'coder' && (
          <CoderContent onShare={openShare} />
        )}
      </ModalBackdrop>

      {shareModalOpen && (
        <ShareTakeHomeModal
          onClose={() => setShareModalOpen(false)}
          onSuccess={() => window.location.reload()}
          continueLabel='View unlocked take-home'
        />
      )}
    </>
  );
}
